import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { atomWithDefault, selectAtom } from 'jotai/utils';
import { createContext } from 'react';

import type { TpslOrderType } from '@flipster/apis/v2/trade/types';
import type { PswapOrder } from '@flipster/r230522/model/prex.v2.r230522.tables.private_orders';
import { StepModel } from '@flipster/react/misc/step/use-steps';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import type { BigNumber } from '@flipster/utils/num';

import { SymbolScope } from '../../shared/symbol/bunja';
import { parentPositionBunja } from './base';
import { resolveInitialStep, resolveTpslModifyTarget, resolveTpslTab } from './tpsl-drag-open';
import {
  deriveTpslIsBbo,
  deriveTpslLimitPrice,
  deriveTpslOrderType,
  deriveTpslQtyPercent,
  deriveTpslTriggerPrice,
  deriveTpslTriggerPriceType,
} from './tpsl-edit-prefill';

export type TpslType = 'tp' | 'sl';

export const TpslTypeContext = createContext<TpslType>('tp');
export const TpslTypeScope = createScopeFromContext(TpslTypeContext);

export const PositionSlotContext = createContext(-1);
export const PositionSlotScope = createScopeFromContext(PositionSlotContext);

export const SelectedTpslOrderContext = createContext<PswapOrder | undefined>(undefined);
export const SelectedTpslOrderScope = createScopeFromContext(SelectedTpslOrderContext);

/**
 * Whether the Limit TP/SL option (the "Limit Order" checkbox) is available.
 * Set to false for environments that must only place Market TP/SL orders
 * (e.g. copy-trading open positions).
 */
export const CanLimitTpslContext = createContext<boolean>(true);
export const CanLimitTpslScope = createScopeFromContext(CanLimitTpslContext);

/**
 * 생성 모드에서 트리거 가격 입력을 prefill할 초기값(Flow A: 차트 드래그로 결정).
 * 지정되지 않으면 진입가/주문가가 기본값으로 쓰인다.
 */
export const InitialTriggerPriceContext = createContext<number | undefined>(undefined);
export const InitialTriggerPriceScope = createScopeFromContext(InitialTriggerPriceContext);

/**
 * 기존 TP/SL order line을 끌어 "수정"으로 모달을 열 때 그 주문 id(Flow B). 지정되면 해당 주문을
 * entire/partial 목록에서 찾아 entire-update / partial-update로 진입한다. 생성 드래그에선 undefined.
 */
export const ModifyOrderIdContext = createContext<string | undefined>(undefined);
export const ModifyOrderIdScope = createScopeFromContext(ModifyOrderIdContext);

export type TpslModalSteps = {
  'entire-add': undefined;
  'entire-update': { tpslOrder: PswapOrder };
  'partial-add': undefined;
  'partial-update': { tpslOrder: PswapOrder };
  'partial-manage': undefined;
};

/**
 * step + (update일 때) orderId만 비교한다. qty/price 등 필드 churn은 무시해 루트 리렌더를 막는다.
 * context.tpslOrder 필드는 stale일 수 있으므로, 수정 진입·prefill은 원본 리스트에서 최신 주문을 다시 읽는다.
 */
function areTpslModalStepsEqual(a: StepModel<TpslModalSteps>, b: StepModel<TpslModalSteps>): boolean {
  if (a.step !== b.step) return false;
  if (a.step === 'entire-update' || a.step === 'partial-update') {
    return a.context.tpslOrder.orderId === (b as typeof a).context.tpslOrder.orderId;
  }
  return true;
}

export const tpslModalBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const positionSlot = bunja.use(PositionSlotScope);
  const symbol = bunja.use(SymbolScope);
  const tpslType = bunja.use(TpslTypeScope);
  // Flow A: 차트 드래그로 모달을 열었는지 여부. 드래그 opener만 InitialTriggerPrice를 넘기므로
  // 이 값의 존재가 "드래그로 열림" 신호 역할을 한다(`tpsl-form-content`의 hasDraggedTriggerPrice와 동일 기준).
  const initialTriggerPriceFromDrag = bunja.use(InitialTriggerPriceScope);
  const openedViaDrag = initialTriggerPriceFromDrag != null && initialTriggerPriceFromDrag > 0;
  // Flow B: 기존 order line을 끌어 수정으로 연 경우의 대상 주문 id(없으면 생성).
  const modifyOrderId = bunja.use(ModifyOrderIdScope);
  const canLimitTpslFromScope = bunja.use(CanLimitTpslScope);
  const {
    positionType,
    parentId,
    sideAtom,
    tpCountAtom,
    slCountAtom,
    entireTpOrdersAtom,
    entireSlOrdersAtom,
    partialTpOrdersAtom,
    partialSlOrdersAtom,
    isLimitOrderAtom,
    isTimeTriggerOrderAtom,
    positionQtyAtom,
    dismissalGuardAtom,
  } = bunja.use(parentPositionBunja);
  // `canLimitTpslAtom` is the final gate on the Limit TP/SL option. Limit TP/SL
  // is fully rolled out on both open positions and pending(Trigger/Limit) orders,
  // so the only remaining gate is the scope/prop, which can force Market-only
  // (e.g. copy-trading open positions).
  const canLimitTpslAtom = atom(() => canLimitTpslFromScope);

  const triggerDirectionAtom = atom((get) => {
    const positionSide = get(sideAtom);
    if (positionSide === 'Long') {
      if (tpslType === 'tp') return 'Up';
      else return 'Down';
    } else {
      if (tpslType === 'tp') return 'Down';
      else return 'Up';
    }
  });

  const tpslOrderSideForOpenPositionAtom = atom((get) => {
    const positionSide = get(sideAtom);
    return positionSide === 'Long' ? 'Short' : 'Long';
  });
  const tpslOrderSideAtom = positionType === 'open' ? tpslOrderSideForOpenPositionAtom : sideAtom;

  const isTimeTriggerOrder = store.get(isTimeTriggerOrderAtom);
  const isLimitOrder = store.get(isLimitOrderAtom);

  const hasTpslOrderAtom = atom<'empty' | 'single' | 'multiple'>((get) => {
    const sharedCountAtom = tpslType === 'tp' ? tpCountAtom : slCountAtom;
    const count = get(sharedCountAtom);
    if (count === 0) return 'empty';
    if (count === 1) return 'single';
    return 'multiple';
  });

  const entireOrdersAtom = tpslType === 'tp' ? entireTpOrdersAtom : entireSlOrdersAtom;
  const partialOrdersAtom = tpslType === 'tp' ? partialTpOrdersAtom : partialSlOrdersAtom;

  const entireOrdersCountAtom = atom((get) => get(entireOrdersAtom).length);
  const partialOrdersCountAtom = atom((get) => get(partialOrdersAtom).length);

  // Presence/identity only: orderId가 같으면 이전 객체를 유지해 루트 리렌더를 막는다.
  // qty/price 등 필드는 stale일 수 있다 → 수정 진입 시 entireOrdersAtom에서 최신 주문을 재조회할 것.
  const entireOrderAtom = selectAtom(
    entireOrdersAtom,
    (orders) => (orders.length > 0 ? orders[0] : undefined),
    (a, b) => a?.orderId === b?.orderId,
  );

  // Flow B: 끌어온 기존 주문(modifyOrderId)을 entire/partial 목록에서 찾는다(없으면 생성으로 폴백).
  const modifyTargetAtom = atom((get) =>
    resolveTpslModifyTarget({
      modifyOrderId,
      entireOrders: get(entireOrdersAtom),
      partialOrders: get(partialOrdersAtom),
    }),
  );

  const tpslTabAtom = atomWithDefault<'entire' | 'partial'>((get) =>
    resolveTpslTab({
      positionType,
      hasTpslOrder: get(hasTpslOrderAtom),
      hasEntireOrder: Boolean(get(entireOrderAtom)),
      openedViaDrag,
      modifyTargetType: get(modifyTargetAtom)?.tab, // Flow B: 수정 대상 주문의 종류로 탭 최우선 고정
    }),
  );

  const initialStepBaseAtom = atom<StepModel<TpslModalSteps>>((get) => {
    // step이 실제로 바뀔 때는 원본 리스트의 최신 주문으로 context를 채운다.
    const entireOrders = get(entireOrdersAtom);
    const partialOrders = get(partialOrdersAtom);
    return resolveInitialStep({
      positionType,
      tpslTab: get(tpslTabAtom),
      hasTpslOrder: get(hasTpslOrderAtom),
      modifyTarget: get(modifyTargetAtom),
      entireOrder: entireOrders.length > 0 ? entireOrders[0] : undefined,
      pendingSelectedOrder: partialOrders.length > 0 ? partialOrders[0] : undefined,
    });
  });
  // orderId/step 동등 시 이전 StepModel 유지(필드 stale 가능). 수정 진입은 리스트 재조회.
  const initialStepAtom = selectAtom(initialStepBaseAtom, (step) => step, areTpslModalStepsEqual);

  const isEntireAtom = atom((get) => get(tpslTabAtom) === 'entire');

  return {
    symbol,
    tpslType,
    parentId,
    positionSlot,
    hasTpslOrderAtom,
    positionType,
    canPartial: positionType === 'open',
    canLimitTpslAtom,
    initialStepAtom,
    tpslTabAtom,
    openedViaDrag,
    isTimeTriggerOrder,
    isLimitOrder,
    positionQtyAtom,
    tpslOrderSideAtom,
    entireOrderAtom,
    entireOrdersAtom,
    partialOrdersAtom,
    entireOrdersCountAtom,
    partialOrdersCountAtom,
    triggerDirectionAtom,
    isEntireAtom,
    dismissalGuardAtom,
  };
});
tpslModalBunja.debugLabel = 'tpslModalBunja';

export const selectedTpslOrderBunja = bunja(() => {
  const selectedTpslOrder = bunja.use(SelectedTpslOrderScope);
  const { positionType, tpslType, positionQtyAtom, isEntireAtom } = bunja.use(tpslModalBunja);
  const { openPositionAtom } = bunja.use(parentPositionBunja);

  const tpslOrderQtyAtom = atom((get) => {
    if (!selectedTpslOrder) return undefined;
    switch (positionType) {
      case 'open': {
        const isEntire = get(isEntireAtom);
        if (isEntire) {
          const openPosition = get(openPositionAtom);
          return openPosition?.quantity;
        }
        return selectedTpslOrder.qty;
      }
      case 'pending':
        return tpslType === 'tp' ? selectedTpslOrder.orderTpOrderQty : selectedTpslOrder.orderSlOrderQty;
      default:
        return undefined;
    }
  });

  const tpslTriggerPriceAtom = atom(() => deriveTpslTriggerPrice(selectedTpslOrder, positionType, tpslType));
  const tpslTriggerPriceTypeAtom = atom(() => deriveTpslTriggerPriceType(selectedTpslOrder, positionType, tpslType));

  // 100% 로 clamp 하지 않는다: partial TP/SL 은 포지션이 줄어도 qty 가 유지되므로 100% 초과가
  // 정상 상태이고, 목록은 그 값을 그대로 보여준다. 폼 prefill 은 소비 측에서 clamp 한다.
  const tpslQtyPercentAtom = atom((get) => deriveTpslQtyPercent(get(tpslOrderQtyAtom), get(positionQtyAtom)));

  // Execution-state readers for the selected TP/SL.
  //
  // Two different row shapes land here and they carry their state differently:
  //
  // - `open` (standalone Position TP/SL row): the row IS the TP/SL order, so the
  //   bracket `orderTp* / orderSl*` fields are all null. Execution state lives
  //   at the row level — `orderType` (STOP_MARKET vs STOP_LIMIT), `price` (the
  //   user-set limit price), and `priceMatchStrategy` (BBO marker). The matcher
  //   stamps `price` at trigger time for BBO rows, so before trigger we rely on
  //   `priceMatchStrategy` to identify BBO; the legacy "Limit + no price"
  //   heuristic only kicks in when `priceMatchStrategy` is absent.
  //
  // - `pending` (Trigger / Limit main order with bracket TP/SL): the row IS the
  //   parent order, and the attached TP/SL is described by the prefixed
  //   `orderTp* / orderSl*` fields. The parent's own `orderType` refers to the
  //   main order, not the TP/SL, so we can't use it here.
  //
  // Atoms resolve to `undefined` in create mode (no selected order), so the
  // modal falls back to its defaults.
  const tpslOrderTypeAtom = atom((): TpslOrderType | undefined =>
    deriveTpslOrderType(selectedTpslOrder, positionType, tpslType),
  );
  const tpslLimitPriceAtom = atom((): BigNumber | undefined =>
    deriveTpslLimitPrice(selectedTpslOrder, positionType, tpslType),
  );
  const tpslIsBboAtom = atom(() => deriveTpslIsBbo(selectedTpslOrder, positionType, tpslType));

  return {
    selectedTpslOrder,
    tpslOrderQtyAtom,
    tpslTriggerPriceAtom,
    tpslTriggerPriceTypeAtom,
    tpslQtyPercentAtom,
    tpslOrderTypeAtom,
    tpslLimitPriceAtom,
    tpslIsBboAtom,
  };
});
selectedTpslOrderBunja.debugLabel = 'selectedTpslOrderBunja';
